/**
 * Promo-code API contract — zod schemas (single source of truth).
 *
 * POST /v1/promo/validate request/response shape. `discount_type`/`discount_value`
 * mirror the discount-engine vocabulary (`shared/src/schemas/discount.ts`
 * `DiscountType`) — kept as a local literal enum here (not imported) so this
 * contract module has zero coupling to the discount-engine schema file; the
 * value set is identical by design (`'pct' | 'amount'`).
 *
 * Source: 2026-06-25-wandero-email-promo-code-discount spec §5.2 / §6.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Promo: POST /v1/promo/validate
// ---------------------------------------------------------------------------

export const PromoValidateRequestSchema = z.object({
  code: z.string().min(1).max(64),
  /** Accepted for forward-compat; P1 ignores it (codes are site-agnostic/global). */
  site_code: z.string().optional(),
});
export type PromoValidateRequest = z.infer<typeof PromoValidateRequestSchema>;

export const PromoValidateResponseSchema = z.object({
  valid: z.boolean(),
  code: z.string(),
  discount_type: z.enum(['pct', 'amount']),
  /** decimal-string (NUMERIC over PostgREST) — never z.number(), preserves money precision. */
  discount_value: z.string(),
  reason: z.enum(['ok', 'not_found', 'expired', 'redeemed']),
});
export type PromoValidateResponse = z.infer<typeof PromoValidateResponseSchema>;
